import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import Seo, { breadcrumbLd } from "@/components/vr/Seo";
import { Clock, ClipboardList } from "lucide-react";

export function TestsList() {
  const [tests, setTests] = useState([]);
  useEffect(() => { api.get("/tests").then((r) => setTests(r.data)); }, []);
  return (
    <div className="vr-container my-8">
      <Seo
        title="Online Tests — Bihar Board Mock Tests (Class 10 & 12)"
        description="Bihar Board 2027 के लिए Chapter-wise एवं Full-syllabus Online Mock Tests — Timer, Marks एवं Instant Result के साथ।"
        canonical="/online-tests"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Online Tests", url: "/online-tests" }])}
      />
      <Breadcrumbs items={[{ label: "Online Tests" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Online Tests</h1>
      <p className="text-slate-600 mt-2 font-hindi">बिहार बोर्ड 2027 के लिए Chapter-wise एवं Full-syllabus Mock Tests।</p>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tests.map((t) => (
          <Link key={t.id} to={`/test/${t.slug}`} className="vr-card p-5" data-testid={`test-${t.slug}`}>
            <div className="flex items-center gap-2 text-xs text-slate-500"><Clock size={12} /> {t.duration_minutes} मिनट · {t.total_marks} अंक</div>
            <h2 className="mt-2 font-display font-bold text-[#0F172A] text-base">{t.title}</h2>
            <div className="mt-1 text-sm text-slate-600 font-hindi line-clamp-2">{t.description}</div>
            <div className="mt-3 inline-flex items-center gap-1 bg-[#1E3A8A] text-white text-xs font-semibold px-3 py-1.5 rounded-md"><ClipboardList size={12} /> Start Test</div>
          </Link>
        ))}
        {tests.length === 0 && <div className="text-slate-500 text-sm col-span-3">Tests जल्द ही उपलब्ध होंगे।</div>}
      </div>
    </div>
  );
}

export function ImportantQuestions() {
  return (
    <div className="vr-container my-8">
      <Seo
        title="Important Questions — Bihar Board Class 10 & 12"
        description="बिहार बोर्ड परीक्षा में बार-बार पूछे जाने वाले महत्वपूर्ण प्रश्न — Class 10 एवं Class 12 सभी विषयों के लिए।"
        canonical="/important-questions"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Important Questions", url: "/important-questions" }])}
      />
      <Breadcrumbs items={[{ label: "Important Questions" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Important Questions</h1>
      <p className="text-slate-600 mt-2 font-hindi">Bihar Board परीक्षा में बार-बार पूछे जाने वाले महत्वपूर्ण प्रश्न।</p>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {[{ c: "class-10", t: "Class 10 Important Questions" }, { c: "class-12", t: "Class 12 Important Questions" }].map((x) => (
          <Link key={x.c} to={`/${x.c}`} className="vr-card p-6" data-testid={`iq-${x.c}`}>
            <ClipboardList className="text-[#DC2626]" size={22} />
            <h2 className="mt-3 font-display font-bold text-lg text-[#0F172A]">{x.t}</h2>
            <div className="mt-1 text-sm text-slate-500 font-hindi">Subject एवं Chapter-wise महत्वपूर्ण प्रश्न।</div>
          </Link>
        ))}
      </div>
      <div className="mt-6 vr-card p-6">
        <div className="font-hindi text-slate-700">विषय-वार Important Questions चुनने के लिए किसी कक्षा में जाएँ। प्रत्येक Notes page में उस chapter के महत्वपूर्ण प्रश्न भी उपलब्ध हैं।</div>
      </div>
    </div>
  );
}

export function PreviousYear() {
  return (
    <div className="vr-container my-8">
      <Seo
        title="Previous Year Questions (PYQ) — Bihar Board"
        description="बिहार बोर्ड के पिछले वर्षों के प्रश्न पत्र, आधिकारिक स्रोत लिंक एवं question-analysis।"
        canonical="/previous-year-questions"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Previous Year Questions", url: "/previous-year-questions" }])}
      />
      <Breadcrumbs items={[{ label: "Previous Year Questions" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Previous Year Questions (PYQ)</h1>
      <p className="text-slate-600 mt-2 font-hindi">बिहार बोर्ड के पिछले वर्षों के प्रश्न पत्रों का संग्रह।</p>
      <div className="mt-6 vr-card p-6 border-l-4 border-amber-500 bg-amber-50">
        <h2 className="font-bold text-amber-900 text-base">आधिकारिक स्रोत नोटिस</h2>
        <div className="mt-2 text-sm text-slate-700 font-hindi">Bihar Board (BSEB) के official prior-year prashn-patra आधिकारिक वेबसाइट पर उपलब्ध हैं। हम Copyright नियमों का पालन करते हुए केवल आधिकारिक link एवं Question-Analysis प्रदान करते हैं। कृपया official PDF <a href="https://biharboardonline.bihar.gov.in/" target="_blank" rel="noreferrer" className="underline text-[#1E3A8A]">biharboardonline.bihar.gov.in</a> से डाउनलोड करें।</div>
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-3">
        {[2024, 2023, 2022, 2021, 2020, 2019].map((y) => (
          <div key={y} className="vr-card p-5" data-testid={`pyq-${y}`}>
            <h2 className="font-display font-bold text-2xl text-[#1E3A8A]">{y}</h2>
            <div className="text-sm text-slate-500">Bihar Board Class 10 & 12 Papers</div>
            <a href="https://biharboardonline.bihar.gov.in/" target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-[#DC2626]">Official Portal →</a>
          </div>
        ))}
      </div>
    </div>
  );
}
